import {
  useRecordContext,
  RecordContextProvider,
  NumberField,
  TabbedShowLayout,
  useTranslate,
} from "react-admin";
import Box from "@mui/material/Box";
import useTaxPostingMode from "../../../hooks/useTaxPostingMode";

const calcDPPPerUnit = (price, disc, taxRate) => {
  const priceValue = Number(price || 0);
  const discValue = Number(disc || 0);
  const grossAfterDisc = priceValue - discValue;

  const parsedRate = Number(taxRate);
  const effectiveRate = Number.isFinite(parsedRate) && parsedRate >= 0 ? parsedRate : 0.11;
  const denominator = 1 + effectiveRate;

  if (denominator === 0) {
    return Math.round(grossAfterDisc);
  }

  return Math.round(grossAfterDisc / denominator);
};

const PurchaseOrderExpandList = () => {
  const record = useRecordContext();
  const t = useTranslate();
  const { isManualTaxPosting } = useTaxPostingMode();
  return (
    <TabbedShowLayout
      syncWithLocation={false}
      sx={{
        m: 4,
        backgroundClip: "padding-box",
        boxShadow: (theme) => {
          return theme.components.MuiPaper
            ? theme.components.MuiPaper.styleOverrides.elevation1.boxShadow
            : undefined;
        },
        borderRadius: "4px",
        bgcolor: "background.paper",
        "& .RaTabbedShowLayout-content": {
          p: 0,
        },
        "& .RaLabeled-label": {
          display: "none",
        },
      }}
    >
      <TabbedShowLayout.Tab label={t("mine.label.product")}>
        <Box
          component="table"
          sx={{
            width: "100%",
            // bgcolor: "rgba(0,0,0,.04)",
            // fontSize: "smaller",
            borderCollapse: "collapse",
            "& th, td": {
              padding: "6px 8px",
              border: "1px solid #dddddd",
            },
          }}
        >
          <thead>
            <tr>
              <th>{t("mine.label.code")}</th>
              <th>{t("mine.label.name")}</th>
              <th style={{ textAlign: "right" }}>
                {t("mine.label.order_quantity")}
              </th>
              <th style={{ textAlign: "right" }}>{t("mine.label.price")}</th>
              {!isManualTaxPosting ? (
                <th style={{ textAlign: "right" }}>{"DPP"}</th>
              ) : null}
              <th style={{ textAlign: "right" }}>
                {t("mine.label.received_quantity")}
              </th>
              <th style={{ textAlign: "right" }}>
                {t("mine.label.not_received_quantity")}
              </th>
            </tr>
          </thead>
          <tbody>
            {record.purchase_order_items.map((item, idx) => {
              return (
                <tr key={item.id}>
                  <td>{item.product.code}</td>
                  <td>{item.product.name}</td>
                  <td style={{ textAlign: "right" }}>
                    <RecordContextProvider value={item}>
                      <NumberField
                        source="quantity"
                        sx={{
                          fontSize: "inherit",
                        }}
                      />
                    </RecordContextProvider>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <RecordContextProvider value={item}>
                      <NumberField
                        source="price"
                        sx={{
                          fontSize: "inherit",
                        }}
                      />
                    </RecordContextProvider>
                  </td>
                  {!isManualTaxPosting ? (
                    <td style={{ textAlign: "right" }}>
                      <RecordContextProvider
                        value={{
                          dpp_price: calcDPPPerUnit(item.price, item.disc, record?.tax_rate),
                        }}
                      >
                        <NumberField
                          source="dpp_price"
                          sx={{
                            fontSize: "inherit",
                          }}
                        />
                      </RecordContextProvider>
                    </td>
                  ) : null}
                  <td style={{ textAlign: "right" }}>
                    <RecordContextProvider value={item}>
                      <NumberField
                        source="received_quantity"
                        sx={{
                          fontSize: "inherit",
                        }}
                      />
                    </RecordContextProvider>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <RecordContextProvider
                      value={{
                        not_received_quantity:
                          item.quantity - item.received_quantity,
                      }}
                    >
                      <NumberField
                        source="not_received_quantity"
                        sx={{
                          fontSize: "inherit",
                        }}
                      />
                    </RecordContextProvider>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Box>
      </TabbedShowLayout.Tab>
      <TabbedShowLayout.Tab label={t("mine.label.journal")}>
        <Box
          component="table"
          sx={{
            width: "100%",
            // bgcolor: "rgba(0,0,0,.04)",
            // fontSize: "smaller",
            borderCollapse: "collapse",
            "& th, td": {
              padding: "6px 8px",
              border: "1px solid #dddddd",
            },
          }}
        >
          <thead>
            <tr>
              <th>COA</th>
              <th>COA Name</th>
              <th>Description</th>
              <th style={{ textAlign: "right" }}>Debit</th>
              <th style={{ textAlign: "right" }}>Credit</th>
            </tr>
          </thead>
          <tbody>
            {(record.transactions ?? []).map((item, idx) => {
              return (
                <tr key={item.id}>
                  <td>{item.coa.code}</td>
                  <td>{item.coa.name}</td>
                  <td>{item.description}</td>
                  <td style={{ textAlign: "right" }}>
                    <RecordContextProvider value={item}>
                      <NumberField
                        source="debit"
                        sx={{
                          fontSize: "inherit",
                        }}
                      />
                    </RecordContextProvider>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <RecordContextProvider value={item}>
                      <NumberField
                        source="credit"
                        sx={{
                          fontSize: "inherit",
                        }}
                      />
                    </RecordContextProvider>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Box>
      </TabbedShowLayout.Tab>
    </TabbedShowLayout>
  );
};

export default PurchaseOrderExpandList;
